/**
 * metrics-client — the Host view's data layer: fetch the daemon's `GET /metrics`
 * surface for the load/CPU time-series and the headline pill (W4 frontend).
 *
 * `/metrics` is a deliberate two-state contract:
 *   - `200 { available: true, cap, sample, history }` once a sample has landed.
 *   - `503 { available: false, reason }` before the first sample is captured.
 *
 * The `503` is NOT a transport failure — it is the honest "not sampling yet"
 * state, so this fetcher folds it into the snapshot as data (`available: false`)
 * rather than throwing. The Host view renders it as a calm waiting state. Any
 * OTHER non-2xx (a real `500`, a network drop) throws {@link ReadApiError} so the
 * view shows an honest error naming the endpoint, exactly like the other reads.
 *
 * Shaped as a {@link TaggedFetcher} so it drives the same cached-polling reducer
 * (`usePolling`) the run/board feeds use; `/metrics` sets no ETag today, so this
 * always reports `ok` and the poll keeps the last good snapshot across blips.
 *
 * `/metrics` is authorized like every other daemon route, so it goes through
 * {@link apiFetch} for the session cookie and the typed `401`/`403`. Note the
 * ordering that matters: an identity failure is raised by the chokepoint BEFORE
 * the `503` fold below, so a signed-out caller can never be shown the calm
 * "not sampling yet" state.
 */

import { ReadApiError, apiFetch } from './api-fetch'
import type { MetricsSnapshot } from './metrics'
import type { TaggedResult } from './polling'

/** The `/metrics` endpoint path — single source so the fetcher and tests agree. */
export const METRICS_ENDPOINT = '/metrics'

/**
 * Fetch and decode `GET /metrics` as a {@link TaggedResult} for `usePolling`.
 * A `200` or a `503 { available: false }` both resolve as `ok` (the latter is a
 * real, renderable state); any other status or a network error throws
 * {@link ReadApiError} naming the endpoint.
 */
export async function fetchMetricsTagged(
  _etag: string | null,
  fetchImpl: typeof fetch = fetch,
  timeoutMs = 10_000
): Promise<TaggedResult<MetricsSnapshot>> {
  const res = await apiFetch({ endpoint: METRICS_ENDPOINT, fetchImpl, timeoutMs })

  // The 503 pre-first-sample body is a valid, renderable snapshot — not an error.
  if (res.ok || res.status === 503) {
    const data = (await res.json()) as MetricsSnapshot
    return { status: 'ok', etag: res.headers.get('etag'), data }
  }
  throw new ReadApiError(METRICS_ENDPOINT, res.status, `GET ${METRICS_ENDPOINT} returned ${res.status}`)
}
